import { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, ChevronDown, Search } from 'lucide-react';
import { useTerritoryStore } from '@/stores/territoryStore';
import { useTerritories } from '@/hooks/useTerritory';
import { DEFAULT_TERRITORY_CODE } from '@/config';

/** Nombre maximum de communes listées à la fois (perf sur ~1268 communes). */
const MAX_RESULTS = 50;

/** Normalise pour une recherche insensible à la casse et aux accents. */
function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * TerritorySelector — pilule territoire du Header, doublée d'un sélecteur.
 *
 * Au premier rendu où la liste des territoires est disponible et qu'aucun
 * territoire n'est actif, sélectionne automatiquement la commune pilote
 * (DEFAULT_TERRITORY_CODE) — ou, à défaut, le premier de la liste. C'est ce
 * qui « réveille » tous les écrans data-driven (useHeatwave/useRenovation/…
 * désactivés tant que activeTerritory est null).
 *
 * Avec ~1268 communes, le dropdown intègre un champ de recherche (par nom ou
 * code INSEE) et limite l'affichage à MAX_RESULTS lignes.
 */
export function TerritorySelector() {
  const { activeTerritory, setActiveTerritory } = useTerritoryStore();
  const { data: territories } = useTerritories();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // ── Auto-chargement du territoire par défaut ──
  useEffect(() => {
    if (activeTerritory || !territories || territories.length === 0) return;
    const fallback =
      territories.find((t) => t.code_insee === DEFAULT_TERRITORY_CODE) ?? territories[0];
    setActiveTerritory(fallback);
  }, [territories, activeTerritory, setActiveTerritory]);

  // ── Sync du territoire actif avec la liste rafraîchie ──
  // Après une ingestion, ['territories'] est invalidée : on répercute les
  // champs frais (has_full_data, last_full_ingest_at, data_millesime) dans le
  // store persisté pour que gate / fraîcheur se mettent à jour.
  useEffect(() => {
    if (!activeTerritory || !territories) return;
    const fresh = territories.find((t) => t.code_insee === activeTerritory.code_insee);
    if (
      fresh &&
      (fresh.has_full_data !== activeTerritory.has_full_data ||
        fresh.last_full_ingest_at !== activeTerritory.last_full_ingest_at ||
        fresh.data_millesime !== activeTerritory.data_millesime)
    ) {
      setActiveTerritory(fresh);
    }
  }, [territories, activeTerritory, setActiveTerritory]);

  // ── Fermeture au clic extérieur ──
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // ── Réinitialise la recherche à chaque ouverture ──
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  // ── Filtrage (nom ou code INSEE, insensible casse/accents) ──
  const all = territories ?? [];
  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return all;
    return all.filter(
      (t) => normalize(t.name).includes(q) || t.code_insee.toLowerCase().includes(q),
    );
  }, [all, query]);

  const visible = filtered.slice(0, MAX_RESULTS);

  if (!activeTerritory) return null;

  const hasChoice = all.length > 0;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => hasChoice && setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-full border border-line bg-surface-subtle px-2.5 py-1 transition-colors hover:border-muted"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Changer de territoire"
      >
        <MapPin size={11} className="flex-shrink-0 text-muted" />
        <span className="font-sans text-[12px] font-medium leading-none text-body">
          {activeTerritory.name}
        </span>
        <span className="font-mono text-[10px] leading-none text-muted">
          INSEE&nbsp;{activeTerritory.code_insee}
        </span>
        {hasChoice && <ChevronDown size={11} className="flex-shrink-0 text-muted" />}
      </button>

      {open && territories && (
        <div className="absolute left-0 top-full z-30 mt-1.5 w-72 overflow-hidden rounded-[11px] border border-line bg-white shadow-card">
          {/* ── Champ de recherche ── */}
          <div className="border-b border-line p-2">
            <div className="relative flex items-center">
              <Search size={13} className="pointer-events-none absolute left-2.5 text-muted" />
              <input
                type="search"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher une commune ou un code INSEE…"
                className="h-8 w-full rounded-[9px] border border-line bg-surface-subtle pl-7 pr-2.5 font-sans text-[12px] text-body placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-navy"
                aria-label="Rechercher un territoire"
              />
            </div>
          </div>

          {/* ── Liste filtrée ── */}
          <ul role="listbox" className="max-h-72 overflow-auto py-1">
            {visible.length === 0 ? (
              <li className="px-3 py-3 text-center font-sans text-[12px] text-muted">
                Aucune commune trouvée.
              </li>
            ) : (
              visible.map((t) => {
                const active = t.code_insee === activeTerritory.code_insee;
                return (
                  <li key={t.id} role="option" aria-selected={active}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTerritory(t);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-surface-subtle ${
                        active ? 'bg-surface-subtle' : ''
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate font-sans text-[12.5px] font-medium text-body">
                          {t.name}
                        </span>
                        {t.has_full_data === false && (
                          <span
                            className="flex-shrink-0 rounded-full bg-surface-subtle px-1.5 py-0.5 font-sans text-[9px] font-medium text-muted ring-1 ring-line"
                            title="Seules les données de base sont chargées"
                          >
                            données de base
                          </span>
                        )}
                      </span>
                      <span className="flex-shrink-0 font-mono text-[10px] text-muted">
                        {t.code_insee}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          {/* ── Compteur ── */}
          <div className="border-t border-line px-3 py-1.5 font-sans text-[10.5px] text-muted">
            {filtered.length > MAX_RESULTS
              ? `${MAX_RESULTS} sur ${filtered.length} communes`
              : `${filtered.length} commune${filtered.length > 1 ? 's' : ''}`}
          </div>
        </div>
      )}
    </div>
  );
}
